<template>
  <modal :name="nombreModal" :adaptive="true" height="auto" width="600px">
    <div class="conf">
      <div class="modal-header conf-cabecera">
        <h4>{{ $t("reordenarTitulo") }}</h4>
      </div>

      <div class="modal-body conf-cuerpo">
        <!-- Lo que va a pasar, antes de pedir nada. Mismo orden que los demas modales de
             confirmar: primero el precio, luego la decision. -->
        <ul class="conf-consecuencias">
          <li>{{ $t("reordenarMandaServidor", { servidor: servidor }) }}</li>
          <li v-if="movidas">{{ $tc("reordenarCambianTecla", movidas, { n: movidas }) }}</li>
          <li v-if="apartadas">{{ $tc("reordenarApartadas", apartadas, { n: apartadas }) }}</li>
          <li>{{ $t("reordenarProductosSeVan") }}</li>
          <li>{{ $t("reordenarNombresNoSeTocan") }}</li>
        </ul>

        <!-- Que categoria acaba en que tecla, terminal por terminal. -->
        <div v-if="movidas || apartadas">
          <p class="reo-titulo">{{ $t("reindexarComoQueda") }}</p>

          <div class="reo-lista">
            <template v-for="t in terminales">
              <p class="reo-terminal" :key="'n' + t.idTpv">{{ t.nombreTpv }}</p>

              <p class="reo-nota" v-if="t.sinSitio" :key="'s' + t.idTpv">{{ $t("reordenarSinSitio") }}</p>

              <ul v-else :key="'l' + t.idTpv">
                <li v-for="m in t.movimientos" :key="'m' + m.idCategory">
                  <span class="reo-nombre">{{ m.nombre || $t("huecoLibre") }}</span>
                  <span class="reo-aparte" v-if="m.motivo === 'apartada'">{{ $t("reordenarSeAparta") }}</span>
                  <span class="reo-tecla">{{ m.de }}</span>
                  <span class="reo-flecha">&rarr;</span>
                  <span class="reo-tecla nueva">{{ m.a }}</span>
                </li>
              </ul>

              <p class="reo-nota" v-if="t.ambiguos && t.ambiguos.length" :key="'a' + t.idTpv">
                {{ $t("reordenarAmbiguos", { nombres: t.ambiguos.join(", ") }) }}
              </p>
            </template>
          </div>
        </div>

        <p class="conf-nota" v-else>{{ $t("reordenarSinCambios") }}</p>

        <p class="conf-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer conf-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button type="button" class="btn btn-verde" :disabled="ocupado || !(movidas || apartadas)" @click="confirmar()">
          {{ $t("reordenar") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
/**
 * Reordenar las categorias de todos los terminales como las tiene el TPV servidor.
 *
 * Es la primera accion de la pantalla: con las teclas cruzadas entre terminales, los
 * demas avisos cuentan mal -un "Unificar" pondria el nombre del servidor encima de otra
 * categoria-. El plan lo calcula la API (`resumen.ordenDistinto` de
 * getBusinessCategories) con la misma funcion que luego lo aplica, asi que lo que se
 * ensena aqui es lo que se hace.
 *
 * Como los demas modales de confirmar, este NO escribe: emite `confirmar` y la pantalla
 * llama a `reorderBusinessCategories`.
 */
export default {
  name: "ReordenarCategoriasModal",

  props: {
    nombreModal: { type: String, default: "modalReordenarCategorias" },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      servidor: "",
      // resumen.ordenDistinto: un elemento por terminal con algo que mover.
      terminales: [],
      error: ""
    };
  },

  computed: {
    movidas() {
      return this.contar("servidor");
    },

    apartadas() {
      return this.contar("apartada");
    }
  },

  methods: {
    /**
     * @param {string} servidor   Nombre del TPV servidor, para decir quien manda.
     * @param {Array}  terminales resumen.ordenDistinto.
     */
    abrir(servidor, terminales) {
      this.servidor = servidor || "";
      this.terminales = terminales || [];
      this.error = "";

      this.$modal.show(this.nombreModal);
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje || this.$t("errorInesperado");
    },

    // Los terminales que no se pueden reordenar no cuentan: no se va a mover nada en ellos.
    contar(motivo) {
      return this.terminales.reduce(function(total, t) {
        if (t.sinSitio) {
          return total;
        }

        return total + (t.movimientos || []).filter(m => m.motivo === motivo).length;
      }, 0);
    },

    confirmar() {
      if (!this.movidas && !this.apartadas) {
        return;
      }

      this.error = "";
      this.$emit("confirmar");
    }
  }
};
</script>

<style scoped>
@import "../confirmar-en-modal.css";

/* ---------------------------------------------------------------------------
   Solo de este modal: la prevision por terminal. Misma lista y misma tecla que
   la de ReindexarGrupoModal, con el nombre del terminal encima de cada tramo.
   --------------------------------------------------------------------------- */

.reo-titulo {
    margin: 0 0 8px;
    font-size: 0.94em;
    font-weight: 500;
    color: #2c3e50;
}

.reo-lista {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e6e9ef;
    border-radius: 3px;
}

.reo-terminal {
    margin: 0;
    padding: 7px 12px;
    background: #f7f9fb;
    border-bottom: 1px solid #e6e9ef;
    font-size: 0.8em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7b8794;
}

.reo-lista ul {
    margin: 0;
    padding: 0;
    list-style: none;
}

.reo-lista li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f2f6;
    font-size: 0.9em;
}

.reo-nombre {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #2c3e50;
}

.reo-aparte {
    flex: 0 0 auto;
    font-size: 0.85em;
    color: #96690f;
}

/* La tecla se pinta como lo que es -una tecla del terminal-, igual que al reindexar. */
.reo-tecla {
    flex: 0 0 auto;
    min-width: 24px;
    padding: 2px 0;
    text-align: center;
    font-family: Menlo, Consolas, monospace;
    font-size: 0.92em;
    border: 1px solid #dde2e9;
    border-radius: 3px;
    background: #f7f9fb;
    color: #8b96a3;
}

.reo-tecla.nueva {
    border-color: #009982;
    background: rgba(0, 153, 130, 0.08);
    color: #00806d;
    font-weight: 600;
}

.reo-flecha {
    flex: 0 0 auto;
    color: #c3cbd5;
}

.reo-nota {
    margin: 0;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f2f6;
    font-size: 0.82em;
    color: #8a6420;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Categorias/ReordenarCategoriasModal.vue